import React, { useState, useEffect } from "react";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
	Button,
	TextField,
	MuiThemeProvider,
	createMuiTheme,
	FormControlLabel,
	Checkbox,
	IconButton,
	Tooltip,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import { useHistory, useParams } from "react-router-dom";
import {
	deleteBoatType,
	getBoatTypeById,
	postBoatType,
	updateBoatType,
} from "../../../../api/boattype";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertInfo, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";

// Create theme for delete button (red)
const theme = createMuiTheme({
	palette: {
		secondary: {
			main: "#F64E60",
		},
	},
});

function getEmptyBoatType() {
	return {
		fullName: "",
		active: true,
		categories: [],
	};
}

export default function EditBoatTypesPage() {
	const [boatType, setBoatType] = useState(getEmptyBoatType());
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const boatTypeId = useParams().id;
	const history = useHistory();

	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		if (!boatTypeId) {
			disableLoadingData();
			return;
		}
		getBoatTypeById(boatTypeId)
			.then((res) => {
				if (res.status === 200) {
					setBoatType(res.data);
					disableLoadingData();
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage: "Could not get boatType.",
				});
				history.push("/boatTypes");
			});
	}, [boatTypeId, disableLoadingData, history]);

	function saveBoatType() {
		if (!boatTypeId) {
			postBoatType(boatType)
				.then((res) => {
					if (res.status === 201) {
						alertSuccess({
							title: "Saved!",
							customMessage: "BoatType successfully created."
						});
						history.push("/boatTypes");
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "Could not save boatType.",
					});
				});
		} else {
			updateBoatType(boatTypeId, boatType)
				.then((res) => {
					if (res.status === 200) {
						alertSuccess({
							title: "Saved!",
							customMessage: "Changes successfully saved.",
						});
						history.push("/boatTypes");
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "Could not save changes.",
					});
				});
		}
	}

	const handleChange = (element) => (event) => {
		setBoatType({ ...boatType, [element]: event.target.value });
	};

	/*
	 * LAS CATEGORIAS DE ESTE TIPO — Senior, Junior, Open en el pati catalan.
	 *
	 * Se editan AQUI y no en el panel de un club porque un tipo de barco es
	 * uno solo para los 48 clubes: si Cubelles renombrara «Junior», se lo
	 * renombraria a Vilanova y a Sitges. Y ademas no son suyas: salen del
	 * reglamento de la clase.
	 *
	 * `scores` es lo unico que de verdad hace algo: la categoria que no puntua
	 * navega y sale en pantalla, pero no gasta puesto en la clasificacion.
	 */
	const categories = boatType.categories ?? [];

	const setCategories = (nuevas) => setBoatType({ ...boatType, categories: nuevas });

	const cambiarCategoria = (i, campo, valor) =>
		setCategories(categories.map((c, j) => (j === i ? { ...c, [campo]: valor } : c)));

	/** Solo una puede ser la de por defecto: marcar una desmarca la anterior. */
	const marcarDefecto = (i) =>
		setCategories(categories.map((c, j) => ({ ...c, default: j === i })));

	if (isLoadingData) return <ContentSkeleton />;
	else
		return (
			<>
				<Card>
					<CardHeader title="Edit boatType"></CardHeader>
					<CardBody>
						<TextField
							id={`fullName`}
							label="Full name"
							value={boatType.fullName}
							onChange={handleChange("fullName")}
							InputLabelProps={{
								shrink: true,
							}}
							margin="normal"
							variant="outlined"
							required
						/>
						<br />
						<br />
						<FormControlLabel
							control={
								<Checkbox
									checked={boatType.active}
									onChange={() =>
										setBoatType({
											...boatType,
											active: !boatType.active,
										})
									}
									name="checkActive"
								/>
							}
							label="Active"
						/>

						<br />
						<br />
						<h5>Categorías</h5>
						<p className="text-muted" style={{ maxWidth: 640 }}>
							Las de la clase, no las de un club: este tipo lo comparten todos los
							clubes que corren en él. La que <b>no puntúa</b> —Open, por ejemplo—
							navega y sale en pantalla, pero no ocupa puesto en la clasificación.
							Cambiar el nombre no toca las regatas ya corridas: cada participante
							guarda la suya.
						</p>

						{categories.map((c, i) => (
							<div key={i} className="d-flex align-items-center" style={{ gap: 16 }}>
								<TextField
									label="Nombre"
									value={c.fullName ?? ""}
									onChange={(e) => cambiarCategoria(i, "fullName", e.target.value)}
									InputLabelProps={{ shrink: true }}
									margin="normal"
									variant="outlined"
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={c.scores !== false}
											onChange={() => cambiarCategoria(i, "scores", c.scores === false)}
										/>
									}
									label="Puntúa"
								/>
								<FormControlLabel
									control={<Checkbox checked={Boolean(c.default)} onChange={() => marcarDefecto(i)} />}
									label="Por defecto"
								/>
								<Tooltip title="Quitar">
									<IconButton onClick={() => setCategories(categories.filter((_, j) => j !== i))}>
										<DeleteIcon />
									</IconButton>
								</Tooltip>
							</div>
						))}

						{!categories.length && (
							<p className="text-muted">
								Sin categorías. Así es como funcionaba hasta ahora: todo el mundo puntúa.
							</p>
						)}

						<Button
							variant="outlined"
							startIcon={<AddIcon />}
							onClick={() => setCategories([...categories, { fullName: "", scores: true, default: false }])}
						>
							Añadir categoría
						</Button>
					</CardBody>
				</Card>
				<Button
					onClick={() => history.push("/boatTypes")}
					variant="outlined"
					style={{ marginRight: "20px" }}
				>
					Back
				</Button>
				<Button
					onClick={() => saveBoatType()}
					variant="outlined"
					color="primary"
					style={{ marginRight: "20px" }}
				>
					Save boatType
				</Button>
				{boatTypeId && (
					<>
						<MuiThemeProvider theme={theme}>
							<Button
								onClick={() => setOpenConfirmDialog(true)}
								variant="outlined"
								color="secondary"
							>
								Delete boatType
							</Button>
						</MuiThemeProvider>

						<ConfirmDialog
							title={"Are you sure you want to delete this boatType?"}
							open={openConfirmDialog}
							setOpen={setOpenConfirmDialog}
							onConfirm={() => {
								deleteBoatType(boatTypeId)
									.then((res) => {
										if (res.status === 204) {
											alertSuccess({
												title: "Deleted!",
												customMessage:
													"BoatType deleted successfully",
											});
											history.push("/boatTypes");
										}
									})
									.catch((error) => {
										alertError({
											error: error,
											customMessage:
												"Could not delete economic sector.",
										});
									});
							}}
						/>
					</>
				)}
			</>
		);
}
